import React, { useState } from "react";
import { CreditCard } from "lucide-react";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import Card from "src/components/common/card/Card";
import CardHeader from "src/components/common/card/CardHeader";
import Button from "src/components/ui/Button";
import Skeleton from "src/components/ui/Skeleton";
import ContactSupportModal from "src/components/modals/ContactSupportModal";
import usePlanUsage from "src/hooks/usePlanUsage";
import { useAppSelector } from "src/redux/hooks";
import { currencyFormatter } from "src/utils/common";
import { formatDateTime } from "src/utils/date-time";

const SettingsBillingPage: React.FC = () => {
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";

  const { planUsage, loading } = usePlanUsage({ enabled: isTenantAdmin });
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);

  const paymentHistory = planUsage?.paymentHistory || [];

  return (
    <DashboardLayout
      meta={{
        title: "Billing | Invyntra",
        description: "View your current plan and payment history.",
        keywords: "billing, plan, payments, settings, invyntra",
      }}
    >
      <div className="mx-auto max-w-4xl space-y-4">
        <div className="flex items-start gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary-100 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300">
            <CreditCard size={26} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Billing
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Your current plan and payment history.
            </p>
          </div>
        </div>

        {!isTenantAdmin ? (
          <Card>
            <div className="p-6 text-center text-sm text-gray-600 dark:text-gray-300">
              Only tenant admins can view billing details.
            </div>
          </Card>
        ) : (
          <>
            <Card>
              <CardHeader
                title="Current plan"
                description="Plan details and expiry timeline."
                actions={
                  <Button onClick={() => setIsSupportModalOpen(true)}>
                    Extend/Renew Plan
                  </Button>
                }
              />
              <div>
                {loading && (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {Array.from({ length: 6 }).map((_, idx) => (
                      <div key={`plan-skeleton-${idx}`} className="space-y-2">
                        <Skeleton className="h-3 w-20" />
                        <Skeleton className="h-4 w-32" />
                      </div>
                    ))}
                  </div>
                )}
                {!loading && (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    <div>
                      <div className="text-xs uppercase text-gray-400">
                        Plan Name
                      </div>
                      <div className="text-base font-semibold text-gray-900 dark:text-gray-100">
                        {planUsage?.plan?.planName || "N/A"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase text-gray-400">
                        Plan Code
                      </div>
                      <div className="text-base font-semibold text-gray-900 dark:text-gray-100">
                        {planUsage?.plan?.planCode || "N/A"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase text-gray-400">
                        Status
                      </div>
                      <div
                        className={`text-base font-semibold ${
                          planUsage?.planStatus === "active"
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {planUsage?.planStatus || "N/A"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase text-gray-400">
                        Starts At
                      </div>
                      <div className="text-base font-semibold text-gray-900 dark:text-gray-100">
                        {formatDateTime(
                          planUsage?.plan?.planStartsAt || "",
                          "DD/MM/YYYY hh:mm:ss A"
                        ) || "N/A"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase text-gray-400">
                        Ends At
                      </div>
                      <div className="text-base font-semibold text-gray-900 dark:text-gray-100">
                        {formatDateTime(
                          planUsage?.plan?.planEndsAt || "",
                          "DD/MM/YYYY hh:mm:ss A"
                        ) || "N/A"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase text-gray-400">
                        Duration
                      </div>
                      <div className="text-base font-semibold text-gray-900 dark:text-gray-100">
                        {planUsage?.plan?.durationInDays
                          ? `${planUsage.plan.durationInDays} days`
                          : "N/A"}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Card>

            <Card>
              <CardHeader
                title="Payment history"
                description="Every plan payment recorded for your workspace."
              />
              {loading && (
                <div className="space-y-3">
                  {Array.from({ length: 3 }).map((_, idx) => (
                    <Skeleton key={`payment-skeleton-${idx}`} className="h-10 w-full" />
                  ))}
                </div>
              )}
              {!loading && paymentHistory.length === 0 && (
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  No payments recorded yet.
                </div>
              )}
              {!loading && paymentHistory.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100 text-left text-xs uppercase tracking-wide text-gray-500 dark:border-gray-700 dark:text-gray-400">
                        <th className="py-2 pr-4 font-medium">Transaction ID</th>
                        <th className="py-2 pr-4 font-medium">Plan</th>
                        <th className="py-2 pr-4 font-medium">Amount</th>
                        <th className="py-2 pr-4 font-medium">Starts At</th>
                        <th className="py-2 pr-4 font-medium">Ends At</th>
                        <th className="py-2 pr-4 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {paymentHistory.map((item, idx) => (
                        <tr key={item.transactionId || idx}>
                          <td className="py-3 pr-4 font-mono text-xs text-gray-600 dark:text-gray-300">
                            {item.transactionId || "-"}
                          </td>
                          <td className="py-3 pr-4 text-gray-900 dark:text-gray-100">
                            {item.planName || "-"}
                          </td>
                          <td className="py-3 pr-4 text-gray-900 dark:text-gray-100">
                            {currencyFormatter(item.price || 0)}
                          </td>
                          <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">
                            {formatDateTime(item.planStartsAt || "", "D MMM YYYY")}
                          </td>
                          <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">
                            {formatDateTime(item.planEndsAt || "", "D MMM YYYY")}
                          </td>
                          <td className="py-3 pr-4">
                            <span
                              className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                                item.status === "active"
                                  ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                                  : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                              }`}
                            >
                              {item.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </>
        )}
      </div>

      <ContactSupportModal
        open={isSupportModalOpen}
        onClose={() => setIsSupportModalOpen(false)}
      />
    </DashboardLayout>
  );
};

export default SettingsBillingPage;
